/**
 * useJobsQuery - Default job fetching using generic search
 *
 * Extracted from useJobs.ts (lines 56-71) for focused data fetching
 * Uses useGenericSearch with #job query pattern
 */

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { JobFilters } from '@/sections/jobs/types';
import { buildStatusQuery } from '@/sections/jobs/utils';
import { ResourceType } from '@/types';

/**
 * Hook for default job query (no search, no risk filter)
 *
 * Fetches jobs using generic search with query pattern from filters.
 * Polls every 5 seconds for real-time updates.
 * Supports server-side sorting via key and desc parameters.
 *
 * DynamoDB Limitation: Cannot combine filter and sort (different GSIs).
 * Priority: Filter over custom sort.
 *
 * Backend API pattern: ?label=Job&query=status:JR&desc=true
 * - label parameter: Resource type (Job)
 * - query parameter: Status filter OR sort field (mutually exclusive)
 * - desc parameter: boolean for descending sort (true) or ascending (false)
 *
 * Query logic (mutually exclusive):
 * - Filter active: "status:JR" (no custom sort)
 * - No filter, sort active: "updated:" (custom sort)
 * - Neither: "#job" (default fallback)
 *
 * @param filter - Job filter configuration
 * @param enabled - Whether query should be enabled
 * @returns TanStack Query result with jobs data
 */
export function useJobsQuery(filter: JobFilters, enabled: boolean) {
  // Build the status query
  const statusQuery = buildStatusQuery(filter.query);

  // Check if filtering is active (not default '#job')
  const hasFilter = statusQuery !== '#job';

  // DynamoDB limitation: Cannot combine filter and sort (different GSIs)
  // When filtering: Use status filter, no custom sort
  // When not filtering: Use custom sort or default
  const query = hasFilter
    ? statusQuery
    : filter.sort
      ? `${String(filter.sort.orderBy)}:`
      : '#job';

  // When filtering: Default to descending (newest first) to match default behavior
  // When not filtering: Use custom sort from filter
  const desc = hasFilter ? true : filter.sort?.descending;

  const {
    data: jobsData,
    refetch: refreshJobsData,
    error: jobsDataError,
    status: jobsDataStatus,
    isFetchingNextPage: isFetchingJobsDataNextPage,
    fetchNextPage: fetchJobsDataNextPage,
    isFetching: isFetchingJobsData,
    hasNextPage: hasNextJobsDataPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query,
      desc,
    },
    { enabled, refetchInterval: 5000 }
  );

  return {
    data: jobsData,
    refetch: refreshJobsData,
    error: jobsDataError,
    status: jobsDataStatus,
    isFetchingNextPage: isFetchingJobsDataNextPage,
    fetchNextPage: fetchJobsDataNextPage,
    isFetching: isFetchingJobsData,
    hasNextPage: hasNextJobsDataPage,
  };
}
